import React, {useEffect, useCallback, useState, useRef} from 'react';
import {AccentColorOptions} from 'epic-ue-theme';
import {getLocale} from '@epic-core/common';
import {TimelineWrapper} from './Timeline.styles';
import root from 'window-or-global';
import {IndustryProps} from './HomepageHeader.types';
import {InView} from 'react-intersection-observer';
import {Typography} from '../../elements';
import {stopFocusOnClick} from 'epic-ue-shared';

export type ClickHandler = (e) => void;
export interface TimelineProps {
    activeIndustry?: string;
    btnClicked?: string;
    autoPlay: boolean;
    universeData: IndustryProps[];
    inUniverse?: boolean;
    accentColor?: AccentColorOptions;
    onClick?: ClickHandler;
}

export const Timeline = ({
    btnClicked,
    activeIndustry,
    universeData,
    accentColor = 'sky',
    autoPlay,
    inUniverse = false,
    onClick
}: TimelineProps): JSX.Element => {
    const [cycle, setCycle] = useState(0);
    const [inView, setInView] = useState(false);

    // Set if we cycled through all industries
    const [finishedLoop, setFinishedLoop] = useState(false);

    // Clicked a passed industry
    const [reverse, setReverse] = useState(false);

    // Toggle a class to make sure the progress bar doesn't animate
    const [dontAnimate, setDontAnimate] = useState(false);

    // Keep tabs on the next industry in this component
    const [nextIndustryClicked, setNextIndustryClicked] = useState('');

    // Get the inudstries and labels from the CMS in order;
    const IndustryIDs: string[] = [];
    const IndustryLabels: string[] = [];

    if (universeData) {
        universeData.map((industry, i) => {
            if (industry) {
                const industryOrigin = industry.origin || '';
                const industryLabel = industry.label || '';
                IndustryIDs.push(industryOrigin);
                IndustryLabels.push(industryLabel);
            }
        });
    }

    const indexOfActiveIndustry = IndustryIDs.indexOf(activeIndustry || '');

    let indexOfNextIndustry;
    if (nextIndustryClicked) {
        indexOfNextIndustry = IndustryIDs.indexOf(nextIndustryClicked);
    }

    useEffect(() => {
        if (inView) {
            setInView(true);
        }
    }, [inView]);

    const useInterval = (callback, delay) => {
        const savedCallback = useRef<any>();

        // Remember the latest callback.
        useEffect(() => {
            savedCallback.current = callback;
        }, [callback]);

        // Set up the interval.
        useEffect(() => {
            const id = setInterval(() => {
                if (savedCallback) {
                    savedCallback.current();
                }
            }, delay);
            return () => clearInterval(id);
        }, [delay]);
    };

    useInterval(() => {
        if (!inView) {
            setCycle(0);
        } else if (!autoPlay) {
            setCycle(cycle);
        } else if (autoPlay && cycle <= 90) {
            setDontAnimate(false);
            setReverse(false);
            setCycle(cycle + 10);
        } else if (cycle >= 100) {
            // Reset local industry state
            setNextIndustryClicked('');
            let nextIndustry = indexOfActiveIndustry + 1;

            // Reset finish loop
            setFinishedLoop(false);
            const amountOfIndustries = IndustryIDs.length;

            //End of the whole cycle
            if (nextIndustry >= amountOfIndustries) {
                //Set finished cycle
                setFinishedLoop(true);

                //Reset active industry to first
                nextIndustry = 0;
            }
            if (onClick && typeof onClick === 'function' && inView) {
                onClick(IndustryIDs[nextIndustry]);
            }

            setDontAnimate(true);
            setCycle(0);
        } else if (inUniverse) {
            setCycle(0);
        }
    }, 1000);

    const visChange = useCallback(
        (inView) => {
            if (inView) {
                setInView(true);
            } else if (!inView) {
                setInView(false);
            }
        },
        [inView, setInView]
    );

    const timelineClick = (e) => {
        if (e) {
            if (e) {
                e.preventDefault();
                //Reset progress
                setCycle(0);
                setDontAnimate(true);

                const nextIndustryId = e.currentTarget.id;
                setNextIndustryClicked(nextIndustryId);

                const NextIndustryIndex = IndustryIDs.indexOf(nextIndustryId);

                // if we click on an industry that has already passed, we need to go in and reset the "passed" classes.
                if (NextIndustryIndex < indexOfActiveIndustry) {
                    setReverse(true);
                    const timelineItems = root.document
                        .getElementById('ue-homepage-timeline')
                        .getElementsByTagName('li');
                    if (timelineItems) {
                        for (let i = 0; i < timelineItems.length; i++) {
                            if (i > NextIndustryIndex - 1) {
                                timelineItems[i].classList.remove('passed');
                            }
                        }
                    }
                }

                if (onClick && typeof onClick === 'function') {
                    onClick(nextIndustryId);
                }
            }
        }
    };

    let timelineElem: (JSX.Element | undefined)[] = [<div key="timeline" />];
    if (IndustryIDs) {
        timelineElem = IndustryIDs.map((industry, i) => {
            let hasPassed = false;
            if (indexOfActiveIndustry > i && !finishedLoop) {
                hasPassed = true;
            }

            if (nextIndustryClicked) {
                if (indexOfNextIndustry > i && !finishedLoop) {
                    hasPassed = true;
                }
            }

            return (
                <li
                    key={`industry-${industry}-timeline-${i}`}
                    className={`industry
                        ${activeIndustry === industry && !reverse ? 'active' : ''}
                        ${hasPassed ? 'passed' : ''}
                        ${
                            cycle === 95 ||
                            (activeIndustry === industry && dontAnimate && inUniverse)
                                ? 'filled'
                                : ''
                        }
                        ${industry}`}>
                    <button
                        id={industry}
                        className={` ${activeIndustry === industry ? 'disabled' : ''} timeline-btn`}
                        disabled={
                            activeIndustry === industry
                                ? true
                                : false || btnClicked === industry
                                ? true
                                : false
                        }
                        onClick={timelineClick}
                        onMouseDown={stopFocusOnClick}>
                        <span className="dot" />
                        <span className={`label ${inUniverse ? 'inUniverse' : ''}`}>
                            <Typography variant="h4">{IndustryLabels[i]}</Typography>
                        </span>
                    </button>
                    <span className={`linewrapper ${dontAnimate ? 'dontAnimate' : ''} `}>
                        <span className="progress"></span>
                        <span className="track"></span>
                    </span>
                </li>
            );
        });
    }

    return (
        <TimelineWrapper
            accentColor={accentColor}
            className={`loc-${getLocale()} ${autoPlay ? 'autoplaying' : ''} 
                ${inUniverse ? 'inUniverse' : ''}
                ${inView ? 'inView' : ''}
            `}
            activeIndustry={activeIndustry || ''}
            cycle={cycle}>
            <InView as="div" onChange={visChange} threshold={0}>
                <ul id="ue-homepage-timeline">{timelineElem}</ul>
            </InView>
        </TimelineWrapper>
    );
};
